<template>
  <div class="locations-gbp-card">
    <!-- Stats Header -->
    <div
      v-if="locations.length > 0"
      class="mb-4">
      <div class="text-sm text-gray-600">
        <span class="font-medium">{{ locations.length }}</span> locations total
        <span
          v-if="checklistStats.created > 0"
          class="ml-2">
          • <span class="font-medium text-green-600">{{ checklistStats.created }}</span> with checklists
        </span>
        <span
          v-if="checklistStats.avgCompletion > 0"
          class="ml-2">
          • <span class="font-medium">{{ checklistStats.avgCompletion }}%</span> avg completion
        </span>
      </div>
    </div>

    <!-- Locations List -->
    <div class="space-y-3">
      <div
        v-for="location in locations"
        :key="location.id"
        class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h4 class="text-lg font-medium text-gray-900">
              {{ location.name }}
              <!-- Checklist Indicator -->
              <span
                v-if="location.gbpChecklist"
                class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                :class="getCompletionClass(location.gbpChecklist.completionPercentage)">
                <i class="fas fa-check-circle mr-1" />
                {{ location.gbpChecklist.completionPercentage }}%
              </span>
            </h4>
            <p class="text-sm text-gray-500 mt-1">
              {{ location.fullAddress || 'No address available' }}
            </p>
            <p
              v-if="location.phoneNumber"
              class="text-sm text-gray-500">
              <i class="fas fa-phone mr-1" />
              {{ location.phoneNumber }}
            </p>
            <!-- Last Updated -->
            <p
              v-if="location.gbpChecklist?.updatedAt"
              class="text-xs text-gray-400 mt-2">
              Last updated: {{ formatDate(location.gbpChecklist.updatedAt) }}
              <span v-if="location.gbpChecklist.updatedBy">
                by {{ location.gbpChecklist.updatedBy }}
              </span>
            </p>
          </div>
          <div class="ml-4 flex space-x-2">
            <button
              class="inline-flex items-center px-3 py-1 border text-sm leading-4 font-medium rounded-md"
              :class="location.gbpChecklist
                ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'"
              @click="openGbpChecklist(location)">
              <i
                class="mr-2"
                :class="location.gbpChecklist ? 'fas fa-edit' : 'fas fa-plus'" />
              {{ location.gbpChecklist ? 'Edit' : 'Create' }} GBP Checklist
            </button>
            <button
              v-if="location.gbpChecklist"
              class="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium
                rounded-md text-gray-700 bg-white hover:bg-gray-50"
              @click="exportPdf(location)">
              <i class="fas fa-file-pdf mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="locations.length === 0"
      class="text-center py-8 text-gray-500">
      <i class="fas fa-map-marker-alt text-4xl mb-3" />
      <p>No locations found for this organization.</p>
    </div>

    <!-- GBP Checklist Modal -->
    <GbpChecklistModal
      v-if="showGbpModal"
      :is-open="showGbpModal"
      :organization-id="organizationId"
      :location-id="selectedLocation?.id"
      :location-name="selectedLocation?.name"
      :locations="locations"
      @close="showGbpModal = false"
      @saved="onChecklistSaved" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import GbpChecklistModal from './gbp_checklist/gbp_checklist_modal.vue'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const locations = ref([])
const showGbpModal = ref(false)
const selectedLocation = ref(null)
const isLoading = ref(false)

// Computed
const checklistStats = computed(() => {
  const total = locations.value.length
  const withChecklists = locations.value.filter((l) => l.gbpChecklist).length
  const totalCompletion = locations.value
    .filter((l) => l.gbpChecklist)
    .reduce((sum, l) => sum + (l.gbpChecklist.completionPercentage || 0), 0)

  return {
    total,
    created: withChecklists,
    avgCompletion: withChecklists > 0 ? Math.round(totalCompletion / withChecklists) : 0
  }
})

// Methods
const loadLocations = async() => {
  isLoading.value = true
  try {
    // Load locations with their GBP checklists
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/locations_with_gbp`)
    locations.value = response.data.locations || []
  } catch (error) {
    // Failed to load locations - handle silently
    locations.value = []
  } finally {
    isLoading.value = false
  }
}

const openGbpChecklist = (location) => {
  selectedLocation.value = location
  showGbpModal.value = true
}

const exportPdf = (location) => {
  if (location.gbpChecklist?.id) {
    window.open(`/ajax/gbp_checklists/${location.gbpChecklist.id}/export`, '_blank')
  }
}

const onChecklistSaved = async() => {
  showGbpModal.value = false
  await loadLocations()
}

const getCompletionClass = (percentage) => {
  if (percentage >= 80) return 'bg-green-100 text-green-800'
  if (percentage >= 50) return 'bg-yellow-100 text-yellow-800'
  return 'bg-gray-100 text-gray-800'
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(() => {
  loadLocations()
})
</script>

<style scoped>
.locations-gbp-card {
  @apply w-full;
}
</style>
